import axios from 'axios'
import Cookies from 'js-cookie'
import router from '@/router'
import qs from 'qs'
import { clearLoginInfo } from '@/utils'
import isPlainObject from 'lodash/isPlainObject'
import { captureError, addBreadcrumb } from '@/utils/sentry'

const http = axios.create({
  baseURL: process.env.NODE_ENV === 'development' ? process.env.VUE_APP_BASE_API : process.env.VUE_APP_API_TARGET + process.env.VUE_APP_BASE_API,
  timeout: 1000 * 180,
})

/**
 * 请求拦截
 */
http.interceptors.request.use(config => {
  config.headers['Accept-Language'] = Cookies.get('language') || 'zh-CN'
  config.headers['token'] = Cookies.get('token') || ''

  // 添加 Sentry 面包屑（追踪 API 请求）
  addBreadcrumb({
    type: 'http',
    category: 'xhr',
    data: {
      method: config.method?.toUpperCase(),
      url: config.url,
    },
    level: 'info'
  })

  // 默认参数
  var defaults = {}
  // 防止缓存，GET请求默认带_t参数
  if (config.method === 'get') {
    config.params = {
      ...config.params,
      ...{ '_t': new Date().getTime() }
    }
  }
  if (isPlainObject(config.params)) {
    config.params = {
      ...defaults,
      ...config.params
    }
  }
  if (isPlainObject(config.data)) {
    config.data = {
      ...defaults,
      ...config.data
    }
    if (/^application\/x-www-form-urlencoded/.test(config.headers['content-type'])) {
      config.data = qs.stringify(config.data)
    }
  }
  return config
}, error => {
  // 请求配置错误
  captureError(error, {
    type: 'request_config_error',
    message: '请求配置错误'
  })
  return Promise.reject(error)
})

/**
 * 响应拦截
 */
http.interceptors.response.use(response => {
  // 添加成功响应的面包屑
  addBreadcrumb({
    type: 'http',
    category: 'xhr',
    data: {
      method: response.config.method?.toUpperCase(),
      url: response.config.url,
      status_code: response.status,
      response_code: response.data?.code
    },
    level: 'info'
  })

  if (response.data.code === 401 || response.data.code === 10001) {
    // 记录未授权错误
    addBreadcrumb({
      type: 'error',
      category: 'auth',
      message: '用户未授权或登录过期',
      data: {
        code: response.data.code,
        msg: response.data.msg
      },
      level: 'warning'
    })

    clearLoginInfo()
    router.replace({ name: 'login' })
    return Promise.reject(response.data.msg)
  }

  return response
}, error => {
  console.error(error)

  // 构建错误上下文
  const errorContext = {
    type: 'api_error',
    url: error.config?.url,
    method: error.config?.method?.toUpperCase(),
    baseURL: error.config?.baseURL,
  }

  // 根据错误类型添加详细信息
  if (error.response) {
    // 服务器返回错误状态码
    errorContext.status = error.response.status
    errorContext.statusText = error.response.statusText
    errorContext.response_data = error.response.data

    // 添加面包屑
    addBreadcrumb({
      type: 'http',
      category: 'xhr',
      data: {
        method: error.config?.method?.toUpperCase(),
        url: error.config?.url,
        status_code: error.response.status,
        status_text: error.response.statusText
      },
      level: 'error'
    })

    // 只对 5xx 服务器错误上报到 Sentry
    if (error.response.status >= 500) {
      captureError(error, errorContext)
    }
  } else if (error.request) {
    // 请求已发送但没有收到响应（网络错误、超时等）
    errorContext.error_type = error.code || 'network_error'
    errorContext.message = error.message

    // 添加面包屑
    addBreadcrumb({
      type: 'http',
      category: 'xhr',
      data: {
        method: error.config?.method?.toUpperCase(),
        url: error.config?.url,
        error_code: error.code,
        error_message: error.message
      },
      level: 'error'
    })

    // 网络错误或超时不上报（太常见）
    // 除非是连续多次失败，可以在业务层处理
  } else {
    // 请求配置出错
    errorContext.error_type = 'request_setup_error'
    errorContext.message = error.message
    captureError(error, errorContext)
  }

  return Promise.reject(error)
})

export default http
